import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom, Observable } from 'rxjs';
import { ErrorFrontend } from '@core/errors';
import { apiRoute } from '@core/utils';
import {
  AgentMessage,
  ConversationCreated,
  ConversationDetail,
  ConversationFolder,
  ConversationListItem,
  ExportLink,
  ExportRef,
  FolderDeleteMode,
  MessageQueued,
  PaginatedResponse,
  PaginationParams,
} from '@core/models';

/**
 * Análisis — the data-analysis agent's chat (`/api/intelligence/`).
 *
 * Asking is asynchronous: a question comes back 202 with its message `queued`;
 * progress arrives on the Análisis page's socket (`agent:event` on
 * `ws/api/intelligence/`, see `AgentSocket`) and the result is
 * read with `message()`. 409 = the user already has a question running, 429 =
 * the organization is at its limit (`ErrorFrontend.status`).
 */
@Injectable({ providedIn: 'root' })
export class IntelligenceService {
  private readonly _http = inject(HttpClient);
  private readonly _baseUrl = 'intelligence';

  private _send<T>(call: Observable<T>): Promise<T> {
    return firstValueFrom(
      call.pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /** The user's conversations, last active first. */
  conversations(params: PaginationParams = {}): Promise<PaginatedResponse<ConversationListItem>> {
    const query: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) query[key] = String(value);
    }
    return this._send(
      this._http.get<PaginatedResponse<ConversationListItem>>(`${this._baseUrl}/conversations/`, {
        params: query,
      }),
    );
  }

  conversation(id: number): Promise<ConversationDetail> {
    return this._send(this._http.get<ConversationDetail>(`${this._baseUrl}/conversations/${id}/`));
  }

  /** A new conversation: its first question. `organization` only when the
   *  user's scope covers more than one (else 400 `ORGANIZATION_REQUIRED`);
   *  `folder` starts it inside one of the user's folders (of that folder's
   *  organization). */
  start(question: string, organization?: number, folder?: number): Promise<ConversationCreated> {
    const body: { question: string; organization?: number; folder?: number } = { question };
    if (organization != null) body.organization = organization;
    if (folder != null) body.folder = folder;
    return this._send(
      this._http.post<ConversationCreated>(`${this._baseUrl}/conversations/`, body),
    );
  }

  /** The next question of a conversation, or the reply to the agent's
   *  clarifying question after a `needs_user` answer. */
  ask(conversationId: number, question: string): Promise<MessageQueued> {
    return this._send(
      this._http.post<MessageQueued>(`${this._baseUrl}/conversations/${conversationId}/messages/`, {
        question,
      }),
    );
  }

  /** Rename a conversation (the backend trims; 400 when empty or too long). */
  renameConversation(id: number, title: string): Promise<ConversationListItem> {
    return this._send(
      this._http.patch<ConversationListItem>(`${this._baseUrl}/conversations/${id}/`, { title }),
    );
  }

  /** Move a conversation into one of the user's folders, or out (`null`). */
  moveConversation(id: number, folder: number | null): Promise<ConversationListItem> {
    return this._send(
      this._http.patch<ConversationListItem>(`${this._baseUrl}/conversations/${id}/`, { folder }),
    );
  }

  /** Delete a conversation and everything Aleia kept of it (a question still
   *  running is stopped). */
  deleteConversation(id: number): Promise<void> {
    return this._send(this._http.delete<void>(`${this._baseUrl}/conversations/${id}/`));
  }

  /** The user's folders, in their order. */
  folders(): Promise<ConversationFolder[]> {
    return this._send(this._http.get<ConversationFolder[]>(`${this._baseUrl}/folders/`));
  }

  createFolder(name: string, organization?: number): Promise<ConversationFolder> {
    const body: { name: string; organization?: number } = { name };
    if (organization != null) body.organization = organization;
    return this._send(this._http.post<ConversationFolder>(`${this._baseUrl}/folders/`, body));
  }

  renameFolder(id: number, name: string): Promise<ConversationFolder> {
    return this._send(
      this._http.patch<ConversationFolder>(`${this._baseUrl}/folders/${id}/`, { name }),
    );
  }

  /** Delete a folder; its conversations move out (`move`) or go with it (`delete`). */
  deleteFolder(id: number, conversations: FolderDeleteMode = 'move'): Promise<void> {
    return this._send(
      this._http.delete<void>(`${this._baseUrl}/folders/${id}/`, { params: { conversations } }),
    );
  }

  /** One message: the polling / reconnect fallback to the socket. */
  message(id: number): Promise<AgentMessage> {
    return this._send(this._http.get<AgentMessage>(`${this._baseUrl}/messages/${id}/`));
  }

  /** Cancel a queued or running question (409 once it finished). */
  cancel(id: number): Promise<AgentMessage> {
    return this._send(this._http.post<AgentMessage>(`${this._baseUrl}/messages/${id}/cancel/`, {}));
  }

  /** Where to download an exported file: its `download_url`, the API route
   *  the message hands out, called with the token. The API answers JSON with
   *  the file's own URL — on S3 a signed URL that expires, so it is asked for
   *  at click time — which the browser opens itself (`openDownload`): never
   *  through HttpClient, since S3 refuses the `Authorization` header and the
   *  CORS of a followed redirect. */
  exportLink(file: ExportRef): Promise<ExportLink> {
    const route = apiRoute(file.download_url);
    if (!route) {
      return Promise.reject(new Error(`Not an API route: ${file.download_url}`));
    }
    return this._send(this._http.get<ExportLink>(route));
  }
}
